import axios from '@/lib/axios'
import { useAccountStore } from '@/stores/account'
import { useMutation } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'
import { ToastInterface, useToast } from 'vue-toastification'

import { MutationParams } from '@/interfaces/mutationParams'

import { User } from '@/modules/users/interfaces/user'

import { useAuth } from './useAuth'
import useCustomMutation from './useCustomMutation'

interface ChangePassword {
	currentPassword?: string
	password: string
	passwordConfirmation: string
}

interface ChangePasswordWithTokenParams {
	password: string
	passwordConfirmation: string
	expires: string
	signature: string
}

interface InviteUserParams {
	name: string
	second_name?: string
	lastname: string
	second_lastname?: string
	gender: string
	email: string
	company: number | string
	expires: string
	signature: string
}

interface InviteMassiveUserParams {
	file: File
	companyId: number | string
}

const changePassword = async (params: ChangePassword) => {
	if (params.currentPassword === '') {
		delete params.currentPassword
	}
	const { data } = await axios.post('/api/user/password', params)
	return data
}

const changePasswordWithToken = async (
	params: ChangePasswordWithTokenParams
) => {
	const baseUrl: string = import.meta.env.VITE_PUBLIC_BACKEND_URL
	const { data } = await axios.post(`${baseUrl}/api/reset-password`, {
		...params,
	})
	return data
}

// `/api/forgot-password` manda el link firmado de `/account/reset-password`, sin
// el correo ni un token en la URL. La ruta web `/forgot-password` exigía no tener
// sesión, y este botón lo usa un administrador con la suya.
const sendPasswordResetEmail = async (params: { email: string }) => {
	const { data } = await axios.post('/api/forgot-password', {
		email: params.email,
	})
	return data
}

const resendVerificationEmail = async (params: { id: string }) => {
	const baseUrl: string = import.meta.env.VITE_PUBLIC_BACKEND_URL
	const { data } = await axios.post(
		`${baseUrl}/email/verification-notification`,
		params
	)
	return data
}

const deactivateAccount = async (params: { id: number }) => {
	const { data } = await axios.delete(`/api/users/${params.id}`)
	return data
}

const reactivateAccount = async (params: { id: number }) => {
	const { data } = await axios.post(`/api/users/${params.id}/restore`)
	return data
}

const handleInviteUser = async (params: {
	email: string
	companyId: number | string
	name: string
}) => {
	const baseUrl: string = import.meta.env.VITE_PUBLIC_BACKEND_URL

	const { data } = await axios.post(`${baseUrl}/api/users/invite`, {
		email: params.email,
		name: params.name,
		company_id: params.companyId,
	})
	return data
}

const registerUserInvitation = async (params: InviteUserParams) => {
	const baseUrl: string = import.meta.env.VITE_PUBLIC_BACKEND_URL

	const { data } = await axios.post(
		`${baseUrl}/api/users/register?company=${params.company}&expires=${params.expires}&signature=${params.signature}`,
		params
	)
	return data
}

const inviteMassiveUser = async (params: InviteMassiveUserParams) => {
	const formData = new FormData()
	formData.append('file', params.file)
	formData.append('company_id', params.companyId.toString())
	const { data } = await axios.post(`api/users/massive-invite`, formData, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
	})
	return data
}

const useAccount = () => {
	const toast = useToast()
	const { user } = useAuth()
	const { resetPasswordSignatureFailed } = storeToRefs(useAccountStore())
	const { setResetPasswordSignatureFailed } = useAccountStore()

	const changePasswordMutation = useMutation(
		({ params }: MutationParams<ChangePassword>) => changePassword(params),
		useCustomMutation(toast, 'update')
	)

	const changePasswordWithTokenMutation = useMutation(
		({ params }: MutationParams<ChangePasswordWithTokenParams>) =>
			changePasswordWithToken(params),
		useCustomMutation(toast, 'update')
	)

	const sendPasswordResetEmailMutation = useMutation(
		({ params }: MutationParams<{ email: string }>) =>
			sendPasswordResetEmail(params),
		useCustomMutation(toast, 'update')
	)

	const resendVerificationEmailMutation = useMutation(
		({ params }: MutationParams<{ id: string }>) =>
			resendVerificationEmail(params),
		useCustomMutation(toast, 'update')
	)

	const deactivateAccountMutation = useMutation(
		({ params }: MutationParams<{ id: number }>) => deactivateAccount(params),
		useCustomMutation(toast, 'delete')
	)

	const reactivateAccountMutation = useMutation(
		({ params }: MutationParams<{ id: number }>) => reactivateAccount(params),
		useCustomMutation(toast, 'update')
	)

	const inviteUserMutation = useMutation(
		({
			params,
		}: MutationParams<{
			email: string
			companyId: number | string
			name: string
		}>) => handleInviteUser(params),
		useCustomMutation(toast, 'create')
	)

	const registerUserInvitationMutation = useMutation(
		({ params }: MutationParams<InviteUserParams>) =>
			registerUserInvitation(params),
		useCustomMutation(toast, 'create')
	)

	const inviteMassiveUserMutation = useMutation(
		({ params }: MutationParams<InviteMassiveUserParams>) =>
			inviteMassiveUser(params),
		useCustomMutation(toast, 'create')
	)

	return {
		resetPasswordSignatureFailed,
		setResetPasswordSignatureFailed,

		changePasswordMutation,
		changePassword: changePasswordMutation.mutate,
		changePasswordAsync: changePasswordMutation.mutateAsync,
		changePasswordLoading: changePasswordMutation.isLoading,

		changePasswordWithTokenMutation,
		changePasswordWithToken: changePasswordWithTokenMutation.mutate,
		changePasswordWithTokenAsync: changePasswordWithTokenMutation.mutateAsync,
		changePasswordWithTokenLoading: changePasswordWithTokenMutation.isLoading,

		sendPasswordResetEmailMutation,
		sendPasswordResetEmail: sendPasswordResetEmailMutation.mutate,
		sendPasswordResetEmailAsync: sendPasswordResetEmailMutation.mutateAsync,
		sendPasswordResetEmailLoading: sendPasswordResetEmailMutation.isLoading,

		resendVerificationEmailMutation,
		resendVerificationEmail: resendVerificationEmailMutation.mutate,
		resendVerificationEmailAsync: resendVerificationEmailMutation.mutateAsync,
		resendVerificationEmailLoading: resendVerificationEmailMutation.isLoading,

		deactivateAccountMutation: deactivateAccountMutation,
		deactivateAccount: deactivateAccountMutation.mutate,
		deactivateAccountAsync: deactivateAccountMutation.mutateAsync,
		deactivateAccountLoading: deactivateAccountMutation.isLoading,
		deactivateAccountSuccess: deactivateAccountMutation.isSuccess,

		reactivateAccountMutation: reactivateAccountMutation,
		reactivateAccount: reactivateAccountMutation.mutate,
		reactivateAccountAsync: reactivateAccountMutation.mutateAsync,
		reactivateAccountLoading: reactivateAccountMutation.isLoading,
		reactivateAccountSuccess: reactivateAccountMutation.isSuccess,

		inviteUserMutation: inviteUserMutation,
		inviteUser: inviteUserMutation.mutate,
		inviteUserAsync: inviteUserMutation.mutateAsync,
		inviteUserLoading: inviteUserMutation.isLoading,
		inviteUserSuccess: inviteUserMutation.isSuccess,

		registerUserInvitationMutation: registerUserInvitationMutation,
		registerUserInvitation: registerUserInvitationMutation.mutate,
		registerUserInvitationAsync: registerUserInvitationMutation.mutateAsync,
		registerUserInvitationLoading: registerUserInvitationMutation.isLoading,
		registerUserInvitationSuccess: registerUserInvitationMutation.isSuccess,

		inviteMassiveUserMutation: inviteMassiveUserMutation,
		inviteMassiveUser: inviteMassiveUserMutation.mutate,
		inviteMassiveUserAsync: inviteMassiveUserMutation.mutateAsync,
		inviteMassiveUserLoading: inviteMassiveUserMutation.isLoading,
	}
}

export default useAccount
